import type { operations } from '@molchanovs/api-contract';
import { z } from 'zod';
import { createQuery } from '@shared/lib/query-helpers';
import { helpdeskTaskSchema } from './commands';

export type HelpdeskThread = Extract<
  operations['getApiV1TasksByTaskUuidReplies']['responses'][200]['content']['application/json'],
  { task: { assignees: unknown } }
>;
export type HelpdeskThreadQuery = NonNullable<
  operations['getApiV1TasksByTaskUuidReplies']['parameters']['query']
>;
export const helpdeskThreadQuerySchema = z
  .object({
    page: z.number().int().min(1).max(100000).default(1),
    pageSize: z.number().int().min(1).max(100).default(20),
  })
  .strict();
const fileSchema = z.object({
  fileRef: z.uuid().nullable(),
  name: z.string(),
  mimeType: z.string().nullable(),
  sizeBytes: z.number().int().nonnegative().nullable(),
  availability: z.enum(['available', 'unavailable']),
});
const messageSchema = helpdeskTaskSchema.extend({ files: z.array(fileSchema) });
export const helpdeskThreadSchema = z.object({
  task: messageSchema,
  items: z.array(
    messageSchema.extend({
      parentId: z.uuid(),
      kind: z.enum(['support-reply', 'user-reply', 'ai-reply', 'internal-comment']),
      signature: z.string().nullable(),
    })
  ),
  meta: z.object({
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
    totalItems: z.number().int().nonnegative(),
    totalPages: z.number().int().nonnegative(),
  }),
}) satisfies z.ZodType<HelpdeskThread>;

export function helpdeskThreadQuery(
  viewerAccessScope: string,
  taskUuid: string,
  query: HelpdeskThreadQuery = {}
) {
  const id = z.uuid().parse(taskUuid);
  const params = helpdeskThreadQuerySchema.parse(query);
  return createQuery<HelpdeskThread>({
    path: `/api/v1/tasks/${encodeURIComponent(id)}/replies`,
    queryKey: ['helpdesk', viewerAccessScope, 'thread', id, params],
    requestOptions: { params },
    schema: helpdeskThreadSchema,
    consumeAbortSignal: false,
    retry: false,
    staleTime: 0,
  });
}
